import { useState, useEffect } from "react";
import { useAuthenticatedApi } from "./useAuthenticatedApi";
import { showErrorToast, getErrorMessage } from "@/lib/toast-utils";
import { unitsResponseCache, buildUnitsCacheKey } from "@/lib/unitsCache";

export interface Unit {
  vin: string;
  serialNumber: string;
  secondaryVin: string;
  make: string;
  modelLine: string;
  model: string;
  modelCode: string;
  modelYear: string;
  unitType: string;
  brandSegment: string;
  warrantyStatus: string;
  retailSaleDate: string;
  unitRegistrationDate: string;
  warrantyExpirationDate: string;
  warrantyExpirationMileage: string;
  unitStatus: string;
  offlineDate: string;
  floorplan?: string;
  warrantyExpireDate?: string;
  warrantyExpired?: boolean;
  mockCode?: string;
  mappedFields: Record<string, boolean>;
}

export interface UnitsResponse {
  units: Unit[];
  totalCount: number;
  pageSize: number;
  currentPage: number;
}

/** True when the API actually provides the property (even if empty/null) */
const exists = (raw: any, ...keys: string[]) =>
  keys.some(k => k in raw && raw[k] !== undefined);

function mapUnitFromApi(raw: any): Unit {
  const mappedFields: Record<string, boolean> = {
    vin: exists(raw, 'vin'),
    serialNumber: exists(raw, 'serialNumber'),
    secondaryVin: exists(raw, 'secondaryVin'),
    make: exists(raw, 'make'),
    modelLine: exists(raw, 'modelLine'),
    model: exists(raw, 'model'),
    modelCode: exists(raw, 'modelCode'),
    unitType: exists(raw, 'unitTypeCode'),
    brandSegment: exists(raw, 'brandSegment'),
    retailSaleDate: exists(raw, 'retailSaleDate'),
    unitRegistrationDate: exists(raw, 'registrationDate'),
    unitStatus: exists(raw, 'status'),
  };

  return {
    vin: raw.vin || '',
    serialNumber: raw.serialNumber || '',
    secondaryVin: raw.secondaryVin || raw.secondaryVIN || '',
    make: raw.make || '',
    modelLine: raw.modelLine || '',
    model: raw.model || '',
    modelCode: raw.modelCode || '',
    modelYear: raw.modelYear || '',
    unitType: raw.unitType || raw.rvType || raw.unitTypeCode || '',
    brandSegment: raw.brandSegment || '',
    warrantyStatus: raw.warrantyExpired !== undefined ? (raw.warrantyExpired ? 'Expired' : 'Active') : '',
    retailSaleDate: raw.retailSaleDate || '',
    unitRegistrationDate: raw.warrantyRegistrationDate || raw.unitRegistrationDate || raw.registrationDate || '',
    warrantyExpirationDate: raw.warrantyExpireDate || raw.warrantyExpirationDate || '',
    warrantyExpirationMileage: raw.warrantyExpirationMileage?.toString() || '',
    unitStatus: raw.status?.name || '',
    offlineDate: raw.offlineDate || '',
    floorplan: raw.floorplan,
    warrantyExpireDate: raw.warrantyExpireDate,
    warrantyExpired: raw.warrantyExpired,
    mockCode: raw.mockCode,
    mappedFields,
  };
}

export const useUnits = (
  customerNumber?: string,
  searchTerm?: string,
  currentCustomerNumber?: string,
  soldByCustomerNumber?: string,
  page: number = 1,
  pageSize: number = 10,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc',
  isUnitSold?: boolean,
  eligibleForWarrantyRegistration?: boolean,
  enabled: boolean = true
) => {
  const [units, setUnits] = useState<Unit[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const { makeAuthenticatedCall } = useAuthenticatedApi();

  const fetchUnits = async () => {
      setIsLoading(true);
      try {
        const cacheKey = buildUnitsCacheKey({ customerNumber, searchTerm, currentCustomerNumber, soldByCustomerNumber, page, pageSize, sortBy, sortDirection, isUnitSold, eligibleForWarrantyRegistration });

        // Check cache first
        if (unitsResponseCache.has(cacheKey)) {
          const cached = unitsResponseCache.get(cacheKey)!;
          setUnits(cached.units);
          setTotalCount(cached.totalCount);
          setIsLoading(false);
          return;
        }

        const { data, error } = await makeAuthenticatedCall<any>('get-units', {
          customerNumber,
          searchTerm,
          currentCustomerNumber,
          soldByCustomerNumber,
          page,
          pageSize,
          sortBy,
          sortDirection,
          isUnitSold,
          eligibleForWarrantyRegistration
        });

        if (error) {
          throw new Error(error);
        }
        
        if (data && data.units && Array.isArray(data.units)) {
          const mapped = data.units.map((raw: any) => mapUnitFromApi(raw));
          setUnits(mapped);
          const count = data.totalCount || mapped.length;
          setTotalCount(count);
          unitsResponseCache.set(cacheKey, { units: mapped, totalCount: count });
        } else if (Array.isArray(data)) {
          const mapped = data.map((raw: any) => mapUnitFromApi(raw));
          setUnits(mapped);
          setTotalCount(data.length);
          unitsResponseCache.set(cacheKey, { units: mapped, totalCount: data.length });
        } else {
          setUnits([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error('Error fetching units:', error);
        setUnits([]);
        setTotalCount(0);
        showErrorToast(getErrorMessage(error, "Failed to fetch units. Please try again."), undefined, fetchUnits);
      } finally {
        setIsLoading(false);
      }
    };

  useEffect(() => {
    if (enabled) {
      fetchUnits();
    }
  }, [customerNumber, searchTerm, currentCustomerNumber, soldByCustomerNumber, page, pageSize, sortBy, sortDirection, isUnitSold, eligibleForWarrantyRegistration, makeAuthenticatedCall, enabled]);

  return { units, isLoading, totalCount, pageSize };
};
